/* eslint-disable react-hooks/exhaustive-deps */
import { GoogleMap } from '@react-google-maps/api';
import { debounce } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleMapContext } from '../../contexts';
import { DEFAULT_CENTER, DEFAULT_ZOOM, extractLatLng } from '../../utils/googleMaps';

export const GoogleMapBox = (props) => {
  const { onChange, size, initialAddress } = props;
  const location = useLocation();
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const markerRef = useRef(null);
  const { isLoaded } = useContext(GoogleMapContext);
  const debouncedGeocodeRef = useRef(null);

  // 마커 업데이트 함수
  const updateMarker = useCallback((mapInstance, position) => {
    if (!mapInstance || !position) return;

    // 기존 마커 제거
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // 새 마커 생성
    const newMarker = new window.google.maps.Marker({
      position: position,
      map: mapInstance,
      animation: window.google.maps.Animation.DROP,
    });

    markerRef.current = newMarker;
  }, []);

  // Geocoding 함수
  const geocodeAddress = useCallback(
    (mapInstance, addressToGeocode) => {
      if (!mapInstance || !addressToGeocode) {
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: addressToGeocode }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location;
          const latLng = extractLatLng(location);

          if (latLng) {
            mapInstance.setCenter(latLng);
            mapInstance.setZoom(DEFAULT_ZOOM);
            setMapCenter(latLng);
            onChange?.(results[0]);
            updateMarker(mapInstance, latLng);
          }
        } else {
          console.error('Geocode was not successful:', status);
        }
      });
    },
    [updateMarker, onChange],
  );

  // Debounced geocode 함수 초기화
  useEffect(() => {
    debouncedGeocodeRef.current = debounce(geocodeAddress, 500);
    return () => {
      if (debouncedGeocodeRef.current) {
        debouncedGeocodeRef.current.cancel();
      }
    };
  }, [geocodeAddress]);

  // Map 로드 핸들러
  const handleMapLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      if (initialAddress) {
        debouncedGeocodeRef.current?.(mapInstance, initialAddress);
      }
    },
    [initialAddress],
  );

  // Map 언마운트 핸들러
  const handleMapUnmount = useCallback(() => {
    // 마커 제거
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    setMap(null);
  }, []);

  useEffect(() => {
    if (map && initialAddress) {
      debouncedGeocodeRef.current?.(map, initialAddress);
    }
  }, [map, initialAddress]);

  // Map container style 계산
  const mapContainerStyle = useMemo(() => {
    if (size) return size;

    const isSmallSize = location.pathname.includes('signup') || location.pathname.includes('mypage');
    return {
      width: isSmallSize ? '560px' : '945px',
      height: '500px',
    };
  }, [size, location.pathname]);

  if (!isLoaded) {
    return null;
  }

  return (
    <div className='flex'>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
        }}
        onLoad={handleMapLoad}
        onUnmount={handleMapUnmount}
      />
    </div>
  );
};

export default React.memo(GoogleMapBox);
